import type { LucideIcon } from "lucide-react";
import {
  AppWindowMac,
  BarChart3,
  ChartLine,
  CreditCard,
  ExternalLink,
  Flame,
  Play,
  Radar,
  Smartphone,
} from "lucide-react";
import React from "react";
import { useSelector } from "react-redux";
import { Constants } from "src/constant";
import { hasPermission, Permission } from "src/lib/roles";
import { selectUserProfile } from "src/store/authSlice";

type Iv3LinkDef = {
  href: string;
  title: string;
  description: string;
  icon: LucideIcon;
  iconClass: string;
};

const IV3_LINKS: Iv3LinkDef[] = [
  {
    href: Constants.IV3_MOBILE_CODE_COVERAGE_URL,
    title: "Code coverage",
    description: "GitLab Pages — coverage and quality reports for IV3 Mobile.",
    icon: BarChart3,
    iconClass: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/25",
  },
  {
    href: Constants.IV3_MOBILE_CRASHLYTICS_URL,
    title: "Crashlytics",
    description:
      "Firebase console for crashes and stability (Google sign-in required).",
    icon: Flame,
    iconClass: "bg-amber-500/15 text-amber-300 ring-amber-400/25",
  },
  {
    href: Constants.IV3_MOBILE_SENTRY_URL,
    title: "Sentry",
    description:
      "Errors, performance, and insights for the ivueit project (Sentry sign-in).",
    icon: Radar,
    iconClass: "bg-violet-500/15 text-violet-300 ring-violet-400/25",
  },
  {
    href: Constants.IV3_MOBILE_DATADOG_URL,
    title: "Datadog",
    description:
      "Logs, metrics, and traces for the ivueit3 project (Datadog sign-in).",
    icon: ChartLine,
    iconClass: "bg-yellow-500/15 text-yellow-300 ring-yellow-400/25",
  },
  {
    href: Constants.IV3_MOBILE_BILLING_URL,
    title: "Billing",
    description:
      "Google Cloud billing and cost reports for the ivueit3 project.",
    icon: CreditCard,
    iconClass: "bg-sky-500/15 text-sky-300 ring-sky-400/25",
  },
  {
    href: Constants.IV3_MOBILE_GOOGLE_PLAY_CONSOLE_URL,
    title: "Google Play Console",
    description:
      "Android app dashboard — releases, store listing, and Play policies.",
    icon: Play,
    iconClass: "bg-green-500/15 text-green-300 ring-green-400/25",
  },
  {
    href: Constants.IV3_MOBILE_APP_STORE_CONNECT_URL,
    title: "App Store Connect",
    description:
      "iOS distribution — TestFlight, App Store submission, and metadata.",
    icon: AppWindowMac,
    iconClass: "bg-blue-500/15 text-blue-300 ring-blue-400/25",
  },
];

const Iv3MobilePanel: React.FC = () => {
  const userProfile = useSelector(selectUserProfile);

  if (
    !userProfile ||
    !hasPermission(userProfile.role, Permission.VIEW_MOBILE_TRACKING)
  ) {
    return (
      <div className="py-6 sm:py-8">
        <h1 className="mb-3 text-2xl font-bold sm:mb-4 sm:text-4xl">
          Access Denied
        </h1>
        <p className="text-sm text-gray-400 sm:text-base">
          You don&apos;t have permission to view IV3 Mobile.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl py-2 sm:py-4">
      <header className="mb-8">
        <h1 className="mb-2 flex items-center gap-2 text-2xl font-bold bg-gradient-to-r from-indigo-400 to-pink-400 bg-clip-text text-transparent sm:gap-3 sm:text-4xl">
          <Smartphone className="h-7 w-7 shrink-0 text-indigo-400 sm:h-8 sm:w-8" />
          IV3 Mobile
        </h1>
        <p className="text-sm text-gray-400 sm:text-base">
          Quick links to external tools. Each opens in a new browser tab.
        </p>
      </header>

      <ul className="flex flex-col gap-3">
        {IV3_LINKS.map(
          ({ href, title, description, icon: Icon, iconClass }) => (
            <li key={href}>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${title} (opens in new tab)`}
                className="group flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 transition-colors hover:border-white/20 hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/80 sm:p-5"
              >
                <span
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ${iconClass}`}
                  aria-hidden
                >
                  <Icon className="h-6 w-6" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 font-semibold text-white">
                    {title}
                    <ExternalLink className="h-4 w-4 shrink-0 text-gray-500 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
                  </span>
                  <span className="mt-1 block text-sm leading-snug text-gray-400">
                    {description}
                  </span>
                </span>
              </a>
            </li>
          ),
        )}
      </ul>
    </div>
  );
};

export default Iv3MobilePanel;
